import type { UseQueryOptions } from '@tanstack/react-query'

import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  PoktanUsahaDataType,
  PoktanUsahaInfiniteQueryOptions,
  PoktanUsahaResponseDataType
} from './types'

const POKTAN_USAHA_QUERY_KEY = (params: {
  page: number
  per_page: number
  search: string
}) => ['reference_poktan_usaha', { ...params }] as const

const getBatch = (
  opts?: Partial<{
    page: number
    per_page: number
    search: string
  }>
): PoktanUsahaInfiniteQueryOptions<
  ReturnType<typeof POKTAN_USAHA_QUERY_KEY>
> => {
  const params = {
    page: opts?.page ?? 1,
    per_page: opts?.per_page ?? 10,
    search: opts?.search ?? ''
  }

  return {
    queryKey: POKTAN_USAHA_QUERY_KEY(params),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const meta = lastPage.meta

      if (!meta) return undefined
      return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
        ? (meta?.current_page ?? 0) + 1
        : undefined
    },
    queryFn: async () => {
      try {
        const res = await api.get<PoktanUsahaResponseDataType>(
          '/report/reference/usaha-poktan',
          {
            params: {
              search: params.search.length >= 2 ? params.search : undefined
            }
          }
        )

        return {
          message: res.data.message,
          data: res.data.data,
          meta: res.data.meta
        }
      } catch (ex) {
        throw handleAxiosError(ex)
      }
    }
  }
}

const POKTAN_USAHA_BY_ID_QUERY_KEY = (params: { id: string }) =>
  ['reference_poktan_usaha', params] as const

const getById = (opts: {
  id: string
}): UseQueryOptions<ServiceGetByIdResponse<PoktanUsahaDataType>> => ({
  queryKey: POKTAN_USAHA_BY_ID_QUERY_KEY(opts),
  queryFn: async () => {
    try {
      const res = await api.get<ServiceGetAllResponse<PoktanUsahaDataType[]>>(
        '/report/reference/usaha-poktan',
        {
          params: {
            id: opts.id
          }
        }
      )

      const data = res.data.data?.[0]
      if (!data) {
        throw new Error('Poktan usaha not found')
      }

      return {
        message: res.data.message,
        data
      }
    } catch (ex) {
      throw handleAxiosError(ex)
    }
  }
})

export const poktanUsahaReference = {
  queryKey: {
    all: POKTAN_USAHA_QUERY_KEY,
    byId: POKTAN_USAHA_BY_ID_QUERY_KEY
  },
  batched: getBatch,
  byId: getById
}
